import { useState } from "react";
import { joinWaitlist } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Check, Loader2 } from "lucide-react";

const AUDIENCES = [
  { v: "agency", label: "Small agency / NFP" },
  { v: "municipality", label: "Municipality" },
  { v: "funder", label: "Funder / foundation" },
  { v: "government", label: "Government (prov/fed)" },
  { v: "other", label: "Other" },
];

// `domain` (optional): the domain-of-care this signup is for, e.g.
// "Mental Health, Substance Use & Crisis". Rendered as a visible tag and
// captured in the submission payload (prefixed onto notes) so the signup is
// attributable to a domain — not just visual.
export default function WaitlistForm({ compact = false, domain, testid = "waitlist-form" }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    email: "",
    name: "",
    organization: "",
    audience: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    const domainTag = domain ? `Domain: ${domain}` : "";
    const notes = domain
      ? form.notes
        ? `${domainTag} — ${form.notes}`
        : domainTag
      : form.notes || undefined;
    try {
      await joinWaitlist({
        email: form.email,
        name: form.name || undefined,
        organization: form.organization || undefined,
        audience: form.audience || undefined,
        notes,
      });
      setDone(true);
      toast({
        title: "You're on the waitlist.",
        description: "We'll be in touch as Continuum reaches each phase.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn't submit",
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testid}-success`}
        className="border border-[#FF8A00] bg-[#FF8A00]/5 p-6 flex items-start gap-4"
      >
        <div className="w-10 h-10 bg-[#FF8A00] flex items-center justify-center flex-shrink-0">
          <Check className="text-[#050A15]" size={20} />
        </div>
        <div>
          <div className="font-bold text-[#0A1224] text-lg">You're on the waitlist.</div>
          <p className="text-slate-600 mt-1">
            We don't email often. When we do, it's because Continuum has reached a phase that
            matters to your sector.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      data-testid={testid}
      onSubmit={onSubmit}
      className={compact ? "space-y-3" : "space-y-4"}
    >
      {domain && (
        <div
          data-testid={`${testid}-domain-tag`}
          className="inline-block bg-[#E2E8F0] text-[#475569] text-xs font-bold px-3 py-1.5"
        >
          Domain: {domain}
        </div>
      )}
      <div className={compact ? "" : "grid md:grid-cols-2 gap-4"}>
        <div>
          <label htmlFor={`${testid}-email`} className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
            Work email <span className="text-[#FF8A00]">*</span>
          </label>
          <input
            id={`${testid}-email`}
            data-testid={`${testid}-email-input`}
            required
            type="email"
            value={form.email}
            onChange={setField("email")}
            placeholder="you@municipality.ca"
            className="w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none focus:ring-0 transition-colors"
          />
        </div>
        {!compact && (
          <div>
            <label htmlFor={`${testid}-name`} className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              Name
            </label>
            <input
              id={`${testid}-name`}
              data-testid={`${testid}-name-input`}
              value={form.name}
              onChange={setField("name")}
              className="w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none transition-colors"
            />
          </div>
        )}
      </div>

      {!compact && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${testid}-org`} className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Organization
              </label>
              <input
                id={`${testid}-org`}
                data-testid={`${testid}-org-input`}
                value={form.organization}
                onChange={setField("organization")}
                className="w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label htmlFor={`${testid}-audience`} className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                I am a…
              </label>
              <select
                id={`${testid}-audience`}
                data-testid={`${testid}-audience-select`}
                value={form.audience}
                onChange={setField("audience")}
                className="w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none transition-colors"
              >
                <option value="">Select…</option>
                {AUDIENCES.map((a) => (
                  <option key={a.v} value={a.v}>{a.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor={`${testid}-notes`} className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              What's the question you'd most want Continuum to answer? <span className="text-slate-400 normal-case font-medium tracking-normal">(optional)</span>
            </label>
            <textarea
              id={`${testid}-notes`}
              data-testid={`${testid}-notes-input`}
              value={form.notes}
              onChange={setField("notes")}
              rows={3}
              className="w-full bg-white border border-slate-300 px-4 py-3 focus:border-[#0A1224] focus:outline-none transition-colors resize-none"
            />
          </div>
        </>
      )}

      <button
        type="submit"
        disabled={loading}
        data-testid={`${testid}-submit`}
        className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : null}
        Join the waitlist
      </button>
      <p className="text-xs text-slate-500 mt-2">
        Canadian-resident data. No third-party marketing. Read our{" "}
        <a href="/privacy" className="underline hover:text-[#0A1224]">privacy policy</a>.
      </p>
    </form>
  );
}
